"use client";

import { useState, useTransition } from "react";
import { Archive, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  runServerZipDownload,
  serverZipKnownToStream,
  serverZipProgressLabel,
  type ServerZipProgress,
} from "@/lib/virtualspace/download-zip-job-client";
import { saveZipResponse } from "@/lib/virtualspace/zip-response-save";

/**
 * Bundle-download trigger with explicit progress states.
 *
 * Why not just an `<a href>` to the zip endpoint?
 * --------------------------------------------------
 * The zip routes generate the archive server-side — for a typical BIM
 * bundle (167 MB RVT + a dozen DWGs + PDFs) that takes ~30 s. With a
 * plain anchor the recipient clicks, sees nothing change in the UI,
 * assumes the click missed, re-clicks, and ends up with two files.
 *
 * This button replaces the anchor with explicit progress:
 *
 *   1. **idle**       — ready to be clicked.
 *   2. **preparing**  — request sent; the server is generating the
 *                       archive. Spinner + "Preparing ZIP…".
 *   3. **saving**     — bytes are flowing. On Chromium they stream
 *                       straight to the file the user picked; elsewhere
 *                       they buffer to a Blob up to a cap, beyond which
 *                       the browser download manager takes over (see
 *                       `zip-response-save.ts`). Copy shows MB saved.
 *   4. **done**       — brief check icon, then back to idle.
 *
 * With `serverZipUrl`, the button first asks that route to build the
 * ZIP on the server (**building**); a large archive then downloads from
 * R2 through the browser's own download manager instead of streaming
 * through a 300 s function. Small archives answer "stream" and take the
 * path above.
 *
 * Errors land in a toast and reset the button to idle so the operator
 * can retry.
 */

interface Props {
  /** URL to the share-token-gated zip endpoint. */
  url: string;
  /** Filename hint for the saved file. The server's
   *  `Content-Disposition` is the authoritative source —
   *  this is just a fallback when that header is missing. */
  filename: string;
  /**
   * Optional override for the idle-state button text. Defaults
   * to "Download all as ZIP" — used by the bundle-wide button.
   * Per-version variants pass "Download V1 as ZIP" / etc. so
   * the recipient knows EXACTLY which payload they're about to
   * spend 30 s waiting on.
   */
  idleLabel?: string;
  /**
   * Visual weight. Customer As-Built "Download all" uses `primary`
   * (VirtualSpace red, larger). Operator / share-page zips stay the
   * quieter muted chip so they don't compete with per-file Downloads.
   */
  emphasis?: "muted" | "primary";
  /**
   * When set, the button shows a `confirm()` dialog with this
   * message before kicking off the download. Used to gate
   * "older version" downloads — the recipient should
   * acknowledge they're not grabbing the latest version (the
   * common operator concern: "they downloaded V1 and treated
   * it as authoritative even though V2 ships the actual fix").
   * `null` / omitted = no confirm step.
   */
  confirmMessage?: string | null;
  /**
   * POST route that builds large archives on the server (operator As-Built
   * package and version ZIPs). Omitted = always stream from `url`.
   */
  serverZipUrl?: string;
}

type State = "idle" | "building" | "preparing" | "saving" | "done";

function isActivationError(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === "SecurityError" || err.name === "NotAllowedError")
  );
}

function formatMb(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / (1024 * 1024)))} MB`;
}

export default function DownloadZipButton({
  url,
  filename,
  idleLabel,
  emphasis = "muted",
  confirmMessage,
  serverZipUrl,
}: Props) {
  const [state, setState] = useState<State>("idle");
  const [build, setBuild] = useState<ServerZipProgress | null>(null);
  const [saved, setSaved] = useState<{ bytes: number; total: number | null } | null>(null);
  const [pending, startTransition] = useTransition();

  function disabled() {
    return state !== "idle" || pending;
  }

  function handleClick() {
    if (disabled()) return;
    // Confirmation gate — only when the caller passed a
    // message. `window.confirm` is intentional (vs. a custom
    // modal): native, accessible, no extra UI surface, and the
    // copy alone is enough to make the recipient pause.
    if (confirmMessage) {
      const ok = window.confirm(confirmMessage);
      if (!ok) return;
    }
    const tryServer = !!serverZipUrl && !serverZipKnownToStream(serverZipUrl);
    setState(tryServer ? "building" : "preparing");
    setSaved(null);
    setBuild(null);
    startTransition(async () => {
      let askedServer = false;
      try {
        if (tryServer && serverZipUrl) {
          askedServer = true;
          const server = await runServerZipDownload({
            requestUrl: serverZipUrl,
            onProgress: setBuild,
          });
          if (server.outcome === "started") {
            toast.success("ZIP is ready. Your browser is downloading it.");
            setState("done");
            setTimeout(() => setState("idle"), 2000);
            return;
          }
          setState("preparing");
        }
        const result = await saveZipResponse({
          url,
          fileName: filename,
          onProgress: (p) => {
            if (p.phase === "saving") {
              setState("saving");
              setSaved({ bytes: p.bytesSaved, total: p.bytesTotal });
            }
          },
        });
        if (result.outcome === "cancelled") {
          setState("idle");
          return;
        }
        setState("done");
        setTimeout(() => setState("idle"), 2000);
      } catch (err) {
        if (askedServer && isActivationError(err)) {
          toast.message("Click the button again to choose where to save the ZIP.");
        } else {
          const msg =
            err instanceof Error ? err.message : "Couldn't generate ZIP";
          toast.error(msg);
        }
        setState("idle");
      } finally {
        setSaved(null);
        setBuild(null);
      }
    });
  }

  const icon = (() => {
    const iconClass = emphasis === "primary" ? "h-4 w-4" : "h-3 w-3";
    if (state === "done") {
      return <Check className={`${iconClass} text-emerald-500`} />;
    }
    if (state === "building" || state === "preparing" || state === "saving") {
      return <Loader2 className={`${iconClass} animate-spin`} />;
    }
    return <Archive className={iconClass} />;
  })();
  const label = (() => {
    if (state === "building") {
      return build ? serverZipProgressLabel(build) : "Preparing ZIP…";
    }
    if (state === "preparing") return "Preparing ZIP…";
    if (state === "saving") {
      if (!saved || saved.bytes === 0) return "Saving…";
      return saved.total
        ? `Saving… ${formatMb(saved.bytes)} of ${formatMb(saved.total)}`
        : `Saving… ${formatMb(saved.bytes)}`;
    }
    if (state === "done") return "Saved";
    return idleLabel ?? "Download all as ZIP";
  })();

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled()}
      className={
        emphasis === "primary"
          ? "vs-btn-solid inline-flex h-11 items-center gap-2 px-4 text-sm disabled:cursor-wait"
          : "inline-flex items-center gap-1.5 rounded-md border vs-border-soft bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-70 disabled:cursor-wait"
      }
      title="Bundle every file in a single ZIP archive (~30s for large BIM bundles)"
    >
      {icon}
      {label}
    </button>
  );
}
